import { Injectable, inject } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject, defer, interval, timer } from 'rxjs';
import { filter, map, reduce, repeatWhen, share, take, withLatestFrom } from 'rxjs/operators';

import { ENVIRONMENT } from '@app/constants/environment.token';

// ─── Array / Object helpers ──────────────────────────────────────────────────

/**
 * Converts an array of objects into a Record keyed by a given field.
 *
 * @param array    - Source array.
 * @param keyField - Property name to use as key.
 * @returns Record mapping `item[keyField]` → item.
 *
 * @example
 * arrayToObject([{ id: 1, tag: 'DRAFT' }], 'id') // { 1: { id: 1, tag: 'DRAFT' } }
 */
export function arrayToObject<T extends object>(array: T[], keyField: keyof T): Record<string, T> {
    return array.reduce<Record<string, T>>((acc, item) => {
        acc[String(item[keyField])] = item;
        return acc;
    }, {});
}

/**
 * Converts the values of a Record into an array, discarding keys.
 *
 * @param object - Source record.
 */
export function objectToArray<T>(object: Record<string, T>): T[] {
    return Object.values(object);
}

/**
 * Returns a shallow copy of the object with all `undefined`, `null` and empty
 * string values removed.
 *
 * @param object - Source object.
 */
export function omitEmpty<T extends Record<string, unknown>>(object: T): Partial<T> {
    const result = { ...object };
    for (const key of Object.keys(result) as (keyof T)[]) {
        const v = result[key];
        if (v === undefined || v === null || v === '') {
            delete result[key];
        }
    }
    return result;
}

// ─── URL helpers ─────────────────────────────────────────────────────────────

/**
 * Returns a shallow copy of `item` with `cover_image.url` percent-decoded.
 * The backend incorrectly returns pre-signed S3 URLs as percent-encoded strings.
 * No-ops when `cover_image` or its `url` is absent.
 *
 * @param item - Any object with an optional `cover_image: { url: string }` field.
 */
export function decodeCoverImageUrl<T extends { cover_image?: { url: string } | null }>(item: T): T {
    if (!item.cover_image?.url) return item;
    return {
        ...item,
        cover_image: { ...item.cover_image, url: decodeURIComponent(item.cover_image.url) },
    };
}

// ─── Array sorting ───────────────────────────────────────────────────────────

/**
 * Returns a new sorted array (ascending) by the given property.
 *
 * @param array           - Source array.
 * @param propertyToSort  - Property name to sort by.
 */
export function sortArray<T>(array: T[], propertyToSort: keyof T): T[] {
    return [...array].sort((a, b) => {
        if (a[propertyToSort] < b[propertyToSort]) return -1;
        if (a[propertyToSort] > b[propertyToSort]) return 1;
        return 0;
    });
}

/**
 * Returns a new sorted array (descending) by the given property.
 *
 * @param array           - Source array.
 * @param propertyToSort  - Property name to sort by.
 */
export function sortArrayDesc<T>(array: T[], propertyToSort: keyof T): T[] {
    return [...array].sort((a, b) => {
        if (a[propertyToSort] > b[propertyToSort]) return -1;
        if (a[propertyToSort] < b[propertyToSort]) return 1;
        return 0;
    });
}

// ─── Duration formatting ──────────────────────────────────────────────────────

/**
 * Formats a number of minutes into a human-readable duration string.
 *
 * @param minutes - Duration in minutes.
 * @returns Formatted string (e.g. "1h 15min", "30min", "2h").
 *
 * @example
 * formatDuration(75) // "1h 15min"
 * formatDuration(30) // "30min"
 * formatDuration(120) // "2h"
 */
export function formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
        return `${mins}min`;
    }

    if (mins === 0) {
        return `${hours}h`;
    }

    return `${hours}h ${mins}min`;
}

// ─── File / binary helpers ───────────────────────────────────────────────────

/**
 * Decodes a base64 string into a `Uint8Array`.
 *
 * @param base64 - Base64-encoded string.
 */
export function base64ToArrayBuffer(base64: string): Uint8Array<ArrayBuffer> {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

/**
 * Triggers a browser file download from a base64-encoded payload.
 *
 * @param fileName - Name of the downloaded file.
 * @param mimeType - MIME type (e.g. `'application/pdf'`).
 * @param base64   - Base64-encoded file content.
 */
export function saveBase64File(fileName: string, mimeType: string, base64: string): void {
    const bytes = base64ToArrayBuffer(base64);
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}

// ─── Timer helpers ───────────────────────────────────────────────────────────

export interface PausableTimer {
    /** Emits at each elapsed second. */
    step: Observable<number>;
    /** Emits once when the countdown/count-up reaches `timeout`. */
    complete: Observable<number>;
}

/**
 * Creates a count-up timer (0 → timeout) that can be paused and restarted.
 *
 * @param timeout       - Total number of seconds to count.
 * @param pauseSubject  - Emitting `true` pauses; `false` resumes.
 * @param restartSubject - Emitting any value restarts from 0.
 */
export function createTimer(timeout: number, pauseSubject: BehaviorSubject<boolean>, restartSubject: Subject<void>): PausableTimer {
    const timer$ = defer(() => {
        let seconds = 0;
        return timer(0, 1000).pipe(
            withLatestFrom(pauseSubject),
            filter(([, paused]) => !paused),
            take(timeout),
            map(() => seconds++),
            repeatWhen(() => restartSubject),
        );
    }).pipe(share());

    return { step: timer$, complete: timer$.pipe(reduce((_, y) => y)) };
}

/**
 * Creates a count-down timer (timeout → 0) that can be paused and restarted.
 *
 * @param timeout       - Total number of seconds to count down from.
 * @param pauseSubject  - Emitting `true` pauses; `false` resumes.
 * @param restartSubject - Emitting any value restarts from `timeout`.
 */
export function createCountdownTimer(
    timeout: number,
    pauseSubject: BehaviorSubject<boolean>,
    restartSubject: Subject<void>,
): PausableTimer {
    const timer$ = defer(() => {
        let seconds = timeout;
        return interval(1000).pipe(
            repeatWhen(() => restartSubject),
            withLatestFrom(pauseSubject),
            filter(([, paused]) => !paused),
            take(timeout),
            map(() => seconds--),
        );
    }).pipe(share());

    return { step: timer$, complete: timer$.pipe(reduce((_, y) => y)) };
}

/**
 * Formats a number of seconds into `{ minutes, seconds }` zero-padded strings.
 *
 * @param remainingSeconds - Elapsed or remaining seconds.
 * @returns `{ minutes: '02', seconds: '05' }` — always 2 digits.
 */
export function formatTimer(remainingSeconds: number): { minutes: string; seconds: string } {
    return {
        minutes: String(Math.floor(remainingSeconds / 60)).padStart(2, '0'),
        seconds: String(remainingSeconds % 60).padStart(2, '0'),
    };
}

// ─── Service ─────────────────────────────────────────────────────────────────

/**
 * General-purpose utilities: forms, PWA detection, routing helpers.
 *
 * Pure array/object/binary/timer helpers are exported as standalone functions
 * at the top of this file — import them directly without injecting this service.
 */
@Injectable({ providedIn: 'root' })
export class UtilsService {
    private readonly router = inject(Router);
    private readonly env = inject(ENVIRONMENT);

    // ─── Form utils ─────────────────────────────────────────────────────────────

    /**
     * Recursively marks all controls in a `FormGroup` as touched, triggering
     * validation error display on every field.
     *
     * @param formGroup - The root `FormGroup` to touch.
     */
    markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach((control: AbstractControl) => {
            control.markAsTouched();
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }

    // ─── PWA utils ───────────────────────────────────────────────────────────────

    /**
     * Returns `true` when the app is running in PWA standalone mode
     * (i.e. installed on the home screen).
     */
    isInStandaloneMode(): boolean {
        return window.matchMedia('(display-mode: standalone)').matches;
    }

    /**
     * Returns `true` when `environment.localMode` is enabled.
     * Used to bypass certain checks during local development.
     */
    isInLocalMode(): boolean {
        return this.env.localMode ?? false;
    }

    // ─── Routing utils ───────────────────────────────────────────────────────────

    /** Returns the current URL path without the leading `/`. */
    getCurrentPath(): string {
        return this.router.url.slice(1);
    }
}
